import { type ReactElement, useMemo } from 'react';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  useFontsLoader
} from '@elfsight/widget-ui';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useSettings } from '@wu/widget-core';
import CATALOG from '../i18n/languages/widget/catalog.json';
import type { StoreLocatorSettings } from './types';
import { ActiveLocationProvider } from './widget/active-location-context';
import { FiltersWithThemeProvider } from './widget/components/filters';
import { LayoutProvider } from './widget/layouts/layout-context';
import { LocationsFilterProvider } from './widget/locations-filter-provider';
import { MapAPIProvider } from './widget/map-api-context';
import { UserLocationProvider } from './widget/user-location-context';
import { Widget } from './widget/widget';
import { WidgetProvider } from './widget/widget-context';

const queryClient = new QueryClient();

export function StoreLocator(_settings: StoreLocatorSettings) {
  return (
    <StoreLocatorProviders>
      <Widget />
    </StoreLocatorProviders>
  );
}

function StoreLocatorProviders({ children }: { children: ReactElement }) {
  const {
    customCSS,
    customJS,
    font,
    widgetId,
    language,
    editMode,
    renderThumbnailMode
  } = useSettings();
  const i18n = useMemo(() => i18nInit(), []);
  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    'store-locator',
    widgetId
  );

  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);
  useFontsLoader([font]);

  const messages = useLoadLocale(language?.language, 'widget', i18n);
  useLanguageControlOverrides({ language, messages, catalog: CATALOG, i18n });

  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <WidgetProvider>
          <FiltersWithThemeProvider>
            <LocationsFilterProvider>
              <UserLocationProvider>
                <MapAPIProvider>
                  <PortalNestProvider
                    customCSSClassName={customCSSPrefixClassName}
                    font={font}
                  >
                    <LayoutProvider>
                      <ActiveLocationProvider>
                        <CustomCSS
                          customCSS={customCSS}
                          prefixClassName={customCSSPrefixClassName}
                        />

                        <EmbedRoot
                          customCSSClassName={customCSSPrefixClassName}
                          font={font}
                        >
                          {children}
                        </EmbedRoot>
                      </ActiveLocationProvider>
                    </LayoutProvider>
                  </PortalNestProvider>
                </MapAPIProvider>
              </UserLocationProvider>
            </LocationsFilterProvider>
          </FiltersWithThemeProvider>
        </WidgetProvider>
      </QueryClientProvider>
    </I18nProvider>
  );
}
